// ============================================================================
// mainThreadProbe.ts
//
// Sonde de saturation du thread principal de la régie.
//
// POURQUOI
//
// « La régie se bloque quand un téléphone se connecte, puis elle finit par
// planter » ne se diagnostique pas depuis un journal d'événements : au moment
// où ça compte, plus rien ne s'exécute, et les logs applicatifs s'arrêtent sans
// rien dire. Ce qu'il faut savoir, c'est ce qui grimpait JUSTE AVANT.
//
// COMMENT
//
// Un intervalle d'une seconde qui mesure sa propre dérive. Un `setInterval`
// réglé sur 1000 ms qui revient au bout de 3200 ms dit que le thread principal
// a été occupé 2200 ms sans rendre la main : c'est la définition opérationnelle
// du figement, et c'est la seule mesure qui reste fiable quand tout le reste
// est bloqué.
//
// À chaque dépassement du seuil, on joint l'état de ce qui peut expliquer la
// charge : PeerConnection vivantes par type et par état, tas JS (Chrome
// uniquement), visibilité de l'onglet. Une dérive qui monte pendant que le
// nombre de PC augmente ne raconte pas la même histoire qu'une dérive qui monte
// à tas constant.
//
// Le coût de la sonde elle-même est nul entre deux dépassements : un
// `Date.now()`, une soustraction, une comparaison. L'inventaire des PC n'est
// construit QUE lorsque le seuil est franchi.
// ============================================================================

import { summarizeLivePeerConnections } from '../liveStreamManagment/api_webrtcBuffers';

/** Période nominale de la sonde. */
const PROBE_INTERVAL_MS = 1000;

/**
 * Dérive à partir de laquelle on considère que le thread a été bloqué.
 * 250 ms : au-dessus du bruit normal d'un rendu React ou d'un GC mineur, en
 * dessous de ce qu'un opérateur perçoit comme un à-coup.
 */
const DRIFT_THRESHOLD_MS = 250;

/**
 * Dérive à partir de laquelle on parle de figement franc plutôt que d'à-coup.
 * Sert uniquement à choisir le niveau de log, donc à décider si la ligne part
 * dans les logs distants.
 */
const FREEZE_THRESHOLD_MS = 1500;

let probeTimer: ReturnType<typeof setInterval> | null = null;
let lastTick = 0;
let worstDrift = 0;

/** Tas JS en Mo — non standard, présent sur Chrome/Edge uniquement. */
const heapSummary = (): string => {
  const mem = (performance as any)?.memory;
  if (!mem?.usedJSHeapSize) return 'tas=indisponible';
  const used  = Math.round(mem.usedJSHeapSize  / 1048576);
  const limit = Math.round(mem.jsHeapSizeLimit / 1048576);
  const pct   = limit > 0 ? Math.round((used / limit) * 100) : 0;
  return `tas=${used}/${limit} Mo (${pct} %)`;
};

/**
 * Démarre la sonde. Idempotent : un second appel ne crée pas un second
 * intervalle. Renvoie la fonction d'arrêt, à appeler au démontage.
 */
export const startMainThreadProbe = (label: string = 'régie'): (() => void) => {
  if (probeTimer !== null) return stopMainThreadProbe;

  lastTick   = Date.now();
  worstDrift = 0;

  probeTimer = setInterval(() => {
    const now   = Date.now();
    const drift = now - lastTick - PROBE_INTERVAL_MS;
    lastTick    = now;

    if (drift < DRIFT_THRESHOLD_MS) return;

    if (drift > worstDrift) worstDrift = drift;

    const detail =
      `thread bloqué ${drift} ms (pire depuis le démarrage : ${worstDrift} ms)`
      + ` | PC vivantes: ${summarizeLivePeerConnections()}`
      + ` | ${heapSummary()}`
      + ` | onglet=${typeof document !== 'undefined' ? document.visibilityState : '?'}`;

    // error au-delà du figement franc : c'est ce niveau qui garantit la remontée
    // dans les logs distants, et c'est la ligne qu'on veut retrouver juste avant
    // un plantage d'onglet.
    if (drift >= FREEZE_THRESHOLD_MS) console.error(`[PROBE ${label}] 🧊 ${detail}`);
    else                              console.warn (`[PROBE ${label}] ⏱ ${detail}`);
  }, PROBE_INTERVAL_MS);

  console.warn(
    `[PROBE ${label}] sonde démarrée — seuil ${DRIFT_THRESHOLD_MS} ms, figement ${FREEZE_THRESHOLD_MS} ms`
  );

  return stopMainThreadProbe;
};

export const stopMainThreadProbe = (): void => {
  if (probeTimer === null) return;
  clearInterval(probeTimer);
  probeTimer = null;
};
